import { Component, Input, OnInit, signal, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

interface AppConfig {
  name: string;
  label: string;
  devUrl: string;
  prodUrl: string;
  icon?: string;
}

@Component({
  selector: 'app-app-switcher',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <button
      (click)="toggleDropdown()"
      class="menu-item w-full flex items-center"
    >
      <app-icon name="apps" [size]="'base'"></app-icon>
      <span class="menu-label">Apps</span>
      <app-icon
        name="chevron-down"
        [size]="'base'"
        class="ml-auto submenu-icon"
        [style.transform]="isDropdownOpen() ? 'rotate(180deg)' : 'rotate(0)'">
      </app-icon>
    </button>

    <!-- Dropdown menu -->
    <ul *ngIf="isDropdownOpen() && !isMenuCollapsed" class="app-switcher-dropdown">
      <li *ngFor="let app of availableApps()">
        <button
          (click)="switchToApp(app)"
          [class.active]="isCurrentApp(app)"
          [class.disabled]="isCurrentApp(app)"
          class="app-switcher-item"
        >
          <app-icon [name]="app.icon || 'circle'" [size]="'base'"></app-icon>
          <span>{{ app.label }}</span>
          <app-icon
            *ngIf="isCurrentApp(app)"
            name="check"
            [size]="'base'"
            class="ml-auto">
          </app-icon>
        </button>
      </li>
    </ul>
  `,
  styles: [`
    :host {
      @apply block relative w-full;
    }

    .menu-item {
      @apply flex gap-3 px-4 py-3 text-sm text-secondary-300 hover:bg-secondary-700/50 transition-colors rounded-lg mx-2;
    }

    .app-switcher-dropdown {
      @apply absolute bottom-full left-0 right-0 mb-2 bg-secondary-800 rounded-lg shadow-lg border border-secondary-700 overflow-hidden z-50;
    }

    .app-switcher-item {
      @apply flex gap-3 px-4 py-3 text-sm text-secondary-300 hover:bg-secondary-700/50 transition-colors w-full text-left items-center;
    }

    .app-switcher-item.active {
      @apply bg-secondary-700 text-white;
    }

    .app-switcher-item.disabled {
      @apply opacity-60 cursor-not-allowed;
    }

    .app-switcher-item:not(.disabled):hover {
      @apply bg-secondary-700/70;
    }
  `]
})
export class AppSwitcherComponent implements OnInit {
  @Input() isMenuCollapsed = false;

  isDropdownOpen = signal<boolean>(false);
  currentApp = signal<AppConfig | null>(null);

  constructor(private elementRef: ElementRef) {}
  availableApps = signal<AppConfig[]>([
    {
      name: 'koppels',
      label: 'Koppels',
      devUrl: 'http://localhost:4201',
      prodUrl: 'https://vandijk-koppels.tccapps.nl',
      icon: 'list'
    },
    {
      name: 'deense-dieren',
      label: 'Deense Dieren',
      devUrl: 'http://localhost:4202',
      prodUrl: 'https://vandijk-deense-dieren.tccapps.nl',
      icon: 'cow'
    },
    {
      name: 'facturatie',
      label: 'Facturatie',
      devUrl: 'http://localhost:4203',
      prodUrl: 'https://vandijk-facturatie.tccapps.nl',
      icon: 'invoices'
    }
  ]);

  ngOnInit() {
    this.detectCurrentApp();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
    }
  }

  detectCurrentApp(): void {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1';

    const apps = this.availableApps();

    if (isDev) {
      // Development: detect based on port
      const portNum = port ? parseInt(port, 10) : (window.location.protocol === 'https:' ? 443 : 80);

      if (portNum === 4201) {
        this.currentApp.set(apps[0]); // koppels
      } else if (portNum === 4202) {
        this.currentApp.set(apps[1]); // deense-dieren
      } else if (portNum === 4203) {
        this.currentApp.set(apps[2]); // facturatie
      } else {
        // Default to first app if port doesn't match
        this.currentApp.set(apps[0]);
      }
    } else {
      // Production: detect based on hostname
      if (hostname.includes('koppels')) {
        this.currentApp.set(apps[0]);
      } else if (hostname.includes('deense-dieren')) {
        this.currentApp.set(apps[1]);
      } else if (hostname.includes('facturatie')) {
        this.currentApp.set(apps[2]);
      } else {
        // Default to first app if hostname doesn't match
        this.currentApp.set(apps[0]);
      }
    }
  }

  isCurrentApp(app?: AppConfig): boolean {
    const current = this.currentApp();
    if (!current) return false;
    if (!app) return true; // If no app provided, check if any app is current
    return current.name === app.name;
  }

  toggleDropdown(): void {
    this.isDropdownOpen.update(value => !value);
  }

  switchToApp(app: AppConfig): void {
    if (this.isCurrentApp(app)) {
      this.isDropdownOpen.set(false);
      return;
    }

    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1';
    const targetUrl = isDev ? app.devUrl : app.prodUrl;

    // Preserve current path if switching within same environment
    const currentPath = window.location.pathname;
    const fullUrl = targetUrl + currentPath;

    window.location.href = fullUrl;
  }
}

